import { Box, Button, Container, Grid, Tooltip, useMediaQuery } from '@mui/material'
import { Icon } from 'components'
import { theme } from 'config'
import { observer } from 'mobx-react'
import { Course, Step, Task } from 'models'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentCourse, QuizCourse, VideoCourse } from 'views'
import { GridCourse } from './gridCourse'
import styled from 'styled-components'
import { TaskType } from 'types'
import { routingStore } from 'app'

export interface ICourseStepProps {
  step: Step
  course: Course
  preview?: boolean
  disableNextButton?: boolean
}

export const CourseStep = observer((props: ICourseStepProps) => {
  const { step, disableNextButton, preview, course } = props
  const { t } = useTranslation()

  const [ratingAdded, setRatingAdded] = React.useState(course?.hasRating)
  const [areColumnAnswersChecked, setAreColumnAnswersChecked] = useState(false)
  const isNotMobileView = useMediaQuery(theme.breakpoints.up('sm'))

React.useEffect(() => {
  if (step?.isQuiz && (step.quiz?.is_control_quiz || step.quiz?.isCurrentTaskColumn) && step.quiz?.isCompleted) {
    step.quiz.reset()
  }
}, [step])


  let view: React.ReactNode
  if (!step) view = null
  else if (step.isGrid) view = <GridCourse step={step} />
  else if (step.isQuiz)
    view = (
      <QuizCourse
        step={step}
        course={course}
        setRatingAdded={setRatingAdded}
        setAreColumnAnswersChecked={setAreColumnAnswersChecked}
      />
    )
  else if (step.isVideo) view = <VideoCourse step={step} />
  else view = <ContentCourse step={step} />

  const getButtonLabel = (): string => {
    if (!step) return 'next'

    // Ensure step.quiz exists before accessing its properties
    if (step.isLast && (!step.isQuiz || step.quiz && step.quiz.alreadyPassed)) {
      if (course.completed) return 'return'
      return 'complete_course'
    }

    if (!step.isQuiz) return t('next')

    // Check for step.quiz existence before using its properties
    if (step.quiz && step.quiz.isCurrentTaskColumn) return t('validate_quiz')

    // It is a quiz
    if (step.quiz && step.quiz.isPassed)
      return t('next')
    if (
      step.quiz &&
      step.quiz.correct_answers > -1 &&
      !step.quiz.is_control_quiz
    )
      return t('retry_quiz')
    if (
      step.quiz &&
      step.quiz.is_control_quiz &&
      curTask?.isComplete &&
      !curTask?.showAnswer
    )
      return t('answer')
    if (step.quiz && step.quiz.isCompleted)
      return preview ? t('generate_new_quiz') : t('validate_quiz')
    return t('next')
  }

  const showNextButton = !step.isSub
  const showBackButton: boolean = !isNotMobileView && step?.isSub

  const curTask: Task = step.quiz?.tasks.length
    ? step.quiz?.tasks[step.quiz.cur_task_idx]
    : null

  const taskCompleted: boolean =
    !(step.quiz?.isCompleted || step.quiz?.alreadyPassed) && curTask
      ? curTask.isComplete
      : true
  return (
    <Container
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        position: 'relative',
      }}
      maxWidth={step.isVideo ? 'xl' : 'md'}
    >
      <div>{view}</div>
      <div>
        {showNextButton && (
          <Grid mt={4} item width={'100%'} xs={12}>
            <Box display='flex' justifyContent='flex-end'>
              <Tooltip
                key='setRating'
                title={
                  !ratingAdded && getButtonLabel() == 'complete_course'
                    ? t('set_rating')
                    : ''
                }
              >
                <span>
                  <Button
                    variant='contained'
                    disabled={
                      routingStore.isLoading ||
                      disableNextButton ||
                      !taskCompleted ||
                      (!ratingAdded && getButtonLabel() == 'complete_course') ||
                      (curTask?.type === TaskType.Column &&
                        !areColumnAnswersChecked)
                    }
                    onClick={() => step?.next(preview)}
                  >
                    {routingStore.isLoading? t("loading"): t(getButtonLabel() )}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        )}
        {showBackButton && (
          <BackButtonContainer
            display='flex'
            alignItems='center'
            position='absolute'
            gap={1}
            p={1}
            top={'2.2rem'}
            zIndex={1100}
            onClick={() =>
              step?.course?.setCurStep(step?.course?.getCurStep?.getParent)
            }
          >
            <Icon iconId='arrow-left' />
            {t('back')}
          </BackButtonContainer>
        )}
      </div>
    </Container>
  )
})

const BackButtonContainer = styled(Box)`
  cursor:   pointer;
`;